import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface LrsRoute {
  id: number;
  route_code: string;
  route_name: string;
  description?: string;
  org_id?: number;
  project_id?: string;
  network_id?: number;
  subnetwork_id?: number;
  origin_measure_m: number;
  length_m: number;
  measure_unit: string;
  geometry?: GeoJSON.LineString;
  event_count?: number;
  chainage_label_end?: string;
  snapshot_id?: number;
  snapshot_at?: string;
  map_ok?: boolean;
  road_ok?: boolean;
  lrs_ok?: boolean;
  map_issues?: LrsQcIssue[];
  road_issues?: LrsQcIssue[];
  lrs_issues?: LrsQcIssue[];
  source?: string;
  /** IRC:73 route-level design attributes */
  design_speed_kmh?: number | null;
  terrain_type?: 'plain' | 'rolling' | 'mountainous' | 'steep' | null;
  row_width_m?: number | null;
  carriageway_config?: string | null;
}

export interface LrsNetwork {
  id: number;
  network_code: string;
  network_name: string;
  project_id?: string;
  org_id?: number;
  description?: string;
  subnetwork_count?: number;
  route_count?: number;
}

export interface LrsSubnetwork {
  id: number;
  network_id: number;
  subnetwork_code: string;
  subnetwork_name: string;
  description?: string;
  route_count?: number;
}

export interface LrsLocateResult {
  route_id: number;
  route_code: string;
  measure_m: number;
  measure_km: number;
  chainage_label: string;
  closest_point?: GeoJSON.Point;
  point?: GeoJSON.Point;
}

export interface LrsEvent {
  id: number;
  route_id: number;
  measure_m: number;
  measure_to_m?: number | null;
  event_type: string;
  label: string;
  chainage_label?: string;
  chainage_to_label?: string;
  is_linear?: boolean;
  side?: string;
  offset_m?: number;
  edge_offset_m?: number;
  grade?: string;
  lifecycle_status?: string;
  condition?: string;
  map_status?: string;
  remaining_life_years?: number | null;
  installation_date?: string | null;
  commissioning_date?: string | null;
  last_inspection_date?: string | null;
  next_inspection_date?: string | null;
  replacement_date?: string | null;
  decommission_date?: string | null;
  expected_life_years?: number | null;
  owner_name?: string | null;
  material?: string | null;
  geometry?: GeoJSON.Point | GeoJSON.LineString;
  properties?: Record<string, unknown>;
  created_at?: string;
}

export interface LrsCalibrationPoint {
  id: number;
  route_id: number;
  measure_m: number;
  point_type: string;
  label?: string;
  chainage_label?: string;
  geometry?: GeoJSON.Point;
}

export interface LrsQcIssue {
  code: string;
  severity: string;
  message: string;
  event_id?: number;
  repair?: { action: string; summary: string };
  other_route_id?: number;
  other_route_code?: string;
}

export interface LrsNetworkQcReport {
  ok: boolean;
  route_count: number;
  pair_count?: number;
  snap_tolerance_m: number;
  gap_max_m?: number;
  issue_count: number;
  issues: LrsQcIssue[];
  repairs: {
    action: string;
    summary: string;
    route_id_a?: number;
    route_id_b?: number;
    code_a?: string;
    code_b?: string;
  }[];
  repair_points?: GeoJSON.FeatureCollection;
  scoped_route_id?: number | null;
}

export interface LrsQcReport {
  route_id: number;
  route_code: string;
  length_m: number;
  max_measure_m: number;
  issue_count: number;
  stale_geometry_count?: number;
  ok: boolean;
  issues: LrsQcIssue[];
  calibration_accuracy?: LrsCalibrationAccuracyReport;
  network?: LrsNetworkQcReport;
}

export interface LrsCalibrationAccuracyReport {
  route_id: number;
  route_code: string;
  anchor_count: number;
  accuracy_pct: number | null;
  within_1m_count: number;
  within_2m_count: number;
  max_drift_m: number | null;
  mean_drift_m: number | null;
  max_error_m_per_km?: number;
  ok_1m_per_km?: boolean;
  ok: boolean;
  target_pct: number;
  message?: string;
  anchors: { id: number; label: string; measure_km: number; drift_m: number; error_m_per_km?: number }[];
}

export interface LrsSurveyDeviationReport {
  route_id: number;
  route_code: string;
  observation_count: number;
  valid_count: number;
  within_tolerance_count: number;
  pass_pct: number | null;
  max_abs_deviation_m: number | null;
  mean_abs_deviation_m: number | null;
  max_lateral_offset_m?: number | null;
  tolerance: {
    max_error_m_per_km: number;
    max_error_pct: number;
    max_abs_deviation_m?: number;
    max_lateral_offset_m?: number;
  };
  ok: boolean;
  observations: {
    index: number;
    label: string;
    field_measure_km?: number;
    lrs_measure_km?: number;
    deviation_m?: number;
    error_m_per_km?: number;
    lateral_offset_m?: number;
    gps_ok?: boolean;
    measure_ok?: boolean;
    offset_ok?: boolean;
    within_tolerance?: boolean;
    error?: string;
  }[];
}

export interface LrsAcceptanceEvidenceReport {
  route_id: number;
  route_code: string;
  generated_at: string;
  ok: boolean;
  checks: { tc: string; title: string; ok: boolean; detail: unknown }[];
  offset?: { measured_offset_m: number | null; target_offset_m: number; ok: boolean };
  survey?: LrsSurveyDeviationReport;
}

export interface LrsBulkImportResult {
  total: number;
  created: number;
  failed: number;
  ok: boolean;
  errors: { row: number; label?: string; error: string }[];
  created_ids: number[];
}

export interface LrsReportCatalogEntry {
  id: string;
  title: string;
  description: string;
  supports_range: boolean;
  formats: string[];
}

export interface LrsExportReportColumn {
  key: string;
  label: string;
}

export interface LrsExportReport {
  meta: {
    report_type: string;
    title: string;
    route_id: number;
    route_code: string;
    route_name: string;
    length_km: number;
    from_km: number | null;
    to_km: number | null;
    generated_at: string;
    standard: string;
    ok?: boolean;
    accuracy_pct?: number | null;
    issue_count?: number;
    [key: string]: unknown;
  };
  columns: LrsExportReportColumn[];
  rows: Record<string, unknown>[];
  summary: Record<string, unknown>;
}

export interface LrsDynamicSegmentResult {
  route: GeoJSON.Feature;
  events: GeoJSON.FeatureCollection;
  segment_length_m: number;
}

export interface LrsSectionLink {
  id: number;
  route_id: number;
  section_id: number;
  from_measure_m: number;
  to_measure_m: number;
  section_code?: string;
  section_name?: string;
}

export interface LrsDocumentRef {
  id: number;
  document_name: string;
  file_name?: string;
  project_id?: number;
  metadata?: { lrs?: Record<string, unknown> } | string;
  created_at?: string;
}

/** Parse `documents.metadata.lrs` from a library or LRS-list row. */
export function lrsMetaFromDocument(
  doc: { metadata?: { lrs?: Record<string, unknown> } | string } | null | undefined
): Record<string, unknown> {
  const raw = doc?.metadata;
  let meta: Record<string, unknown> = {};
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    meta = raw as Record<string, unknown>;
  } else if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') meta = parsed as Record<string, unknown>;
    } catch {
      meta = {};
    }
  }
  const lrs = meta['lrs'];
  return lrs && typeof lrs === 'object' && !Array.isArray(lrs) ? (lrs as Record<string, unknown>) : {};
}

export function measureKmFromLrsMeta(lrs: Record<string, unknown>): number | null {
  const km = Number(lrs['measure_km']);
  if (Number.isFinite(km)) return km;
  const m = Number(lrs['measure_m']);
  if (Number.isFinite(m)) return m / 1000;
  return null;
}

export function lrsChainageLabel(lrs: Record<string, unknown>): string {
  if (lrs['chainage']) return String(lrs['chainage']);
  if (lrs['reference']) return String(lrs['reference']);
  if (lrs['from_km'] != null && lrs['to_km'] != null) {
    return `${lrs['from_km']}–${lrs['to_km']} km`;
  }
  const km = measureKmFromLrsMeta(lrs);
  return km != null ? `km ${km.toFixed(3)}` : '';
}

export interface LrsClusterFeatureProps {
  cluster: boolean;
  count: number;
  id?: number;
  event_type?: string;
  label?: string;
  event_types?: string[];
  sample_ids?: number[];
  level?: 'cluster' | 'asset' | 'type';
  utility_type?: string;
  weight?: number;
}

export interface LrsClusterCollection {
  type: 'FeatureCollection';
  features: GeoJSON.Feature<GeoJSON.Point, LrsClusterFeatureProps>[];
  meta: {
    method?: string;
    eps_m?: number;
    cell_m?: number;
    min_points?: number;
    cluster_count?: number;
    singleton_count?: number;
    total_groups: number;
    max_count?: number;
    networks?: Record<string, { groups: number; assets: number }>;
  };
}

export interface LrsLane {
  id: number;
  route_id: number;
  carriageway_id?: number | null;
  lane_code?: string;
  lane_number?: number;
  lane_type: string;
  transition_type: 'through' | 'merge' | 'diverge' | 'weave';
  direction: string;
  from_measure_m: number;
  to_measure_m: number;
  start_offset_m: number;
  end_offset_m: number;
  width_m?: number;
  geometry?: GeoJSON.LineString;
}

export interface LrsRouteSegment {
  id: number;
  route_id: number;
  seq: number;
  segment_code?: string;
  from_measure_m?: number;
  to_measure_m?: number;
  geometry?: GeoJSON.LineString;
}

@Injectable({ providedIn: 'root' })
export class LrsService {
  private readonly apiUrl = `${environment.BASE_URL}lrs`;

  constructor(private http: HttpClient) {}

  health(): Observable<{ success: boolean }> {
    return this.http.get<{ success: boolean }>(`${this.apiUrl}/health`);
  }

  seedDemo(opts?: { project_id?: string; replace?: boolean }): Observable<{
    success: boolean;
    data: LrsDemoSeedResult;
    message?: string;
  }> {
    return this.http.post<{ success: boolean; data: LrsDemoSeedResult; message?: string }>(
      `${this.apiUrl}/demo/seed`,
      {
        project_id: opts?.project_id,
        replace: opts?.replace !== false,
      }
    );
  }

  getGeoserverInfo(): Observable<{ success: boolean; data: Record<string, unknown> }> {
    return this.http.get<{ success: boolean; data: Record<string, unknown> }>(`${this.apiUrl}/geoserver`);
  }

  listRoutes(params?: { project_id?: string }): Observable<{ success: boolean; data: LrsRoute[] }> {
    let httpParams = new HttpParams();
    if (params?.project_id) {
      httpParams = httpParams.set('project_id', params.project_id);
    }
    return this.http.get<{ success: boolean; data: LrsRoute[] }>(`${this.apiUrl}/routes`, {
      params: httpParams,
    });
  }

  createRoute(body: {
    route_code: string;
    route_name: string;
    geometry: GeoJSON.LineString;
    description?: string;
    project_id?: string;
    origin_measure_m?: number;
    network_id?: number;
    subnetwork_id?: number;
    design_speed_kmh?: number | null;
    terrain_type?: string | null;
    row_width_m?: number | null;
    carriageway_config?: string | null;
  }): Observable<{ success: boolean; data: LrsRoute }> {
    return this.http.post<{ success: boolean; data: LrsRoute }>(`${this.apiUrl}/routes`, body);
  }

  listNetworks(params?: { project_id?: string }): Observable<{ success: boolean; data: LrsNetwork[] }> {
    let httpParams = new HttpParams();
    if (params?.project_id) httpParams = httpParams.set('project_id', params.project_id);
    return this.http.get<{ success: boolean; data: LrsNetwork[] }>(`${this.apiUrl}/networks`, { params: httpParams });
  }

  createNetwork(body: {
    network_code: string;
    network_name: string;
    project_id?: string;
    description?: string;
  }): Observable<{ success: boolean; data: LrsNetwork }> {
    return this.http.post<{ success: boolean; data: LrsNetwork }>(`${this.apiUrl}/networks`, body);
  }

  listSubnetworks(networkId: number): Observable<{ success: boolean; data: LrsSubnetwork[] }> {
    return this.http.get<{ success: boolean; data: LrsSubnetwork[] }>(
      `${this.apiUrl}/networks/${networkId}/subnetworks`
    );
  }

  createSubnetwork(
    networkId: number,
    body: { subnetwork_code: string; subnetwork_name: string; description?: string }
  ): Observable<{ success: boolean; data: LrsSubnetwork }> {
    return this.http.post<{ success: boolean; data: LrsSubnetwork }>(
      `${this.apiUrl}/networks/${networkId}/subnetworks`,
      body
    );
  }

  applyNetworkRepairs(opts?: {
    project_id?: string;
    route_id?: number;
    snap_m?: number;
    gap_max_m?: number;
  }): Observable<{ success: boolean; data: { applied: unknown[]; skipped: unknown[]; after: LrsNetworkQcReport } }> {
    return this.http.post<{ success: boolean; data: { applied: unknown[]; skipped: unknown[]; after: LrsNetworkQcReport } }>(
      `${this.apiUrl}/qc/network/apply`,
      opts || {}
    );
  }

  updateRoute(
    routeId: number,
    body: {
      route_code?: string;
      route_name?: string;
      description?: string | null;
      origin_measure_m?: number;
      length_m?: number;
      sync_length_from_geometry?: boolean;
      map_ok?: boolean;
      design_speed_kmh?: number | null;
      terrain_type?: string | null;
      row_width_m?: number | null;
      carriageway_config?: string | null;
    }
  ): Observable<{ success: boolean; data: LrsRoute }> {
    return this.http.put<{ success: boolean; data: LrsRoute }>(`${this.apiUrl}/routes/${routeId}`, body);
  }

  importFromOsm(body: {
    bbox: [number, number, number, number];
    route_ref?: string;
    route_code?: string;
    route_name?: string;
    project_id?: string;
    network_id?: number;
    subnetwork_id?: number;
  }): Observable<{ success: boolean; data: LrsRoute }> {
    return this.http.post<{ success: boolean; data: LrsRoute }>(`${this.apiUrl}/import/osm`, body);
  }

  importFromRoadProject(
    projectId: string,
    body?: { route_code?: string; route_name?: string; network_id?: number; subnetwork_id?: number }
  ): Observable<{ success: boolean; data: { route: LrsRoute; sections_linked: number } }> {
    return this.http.post<{ success: boolean; data: { route: LrsRoute; sections_linked: number } }>(
      `${this.apiUrl}/import/road-project/${projectId}`,
      body || {}
    );
  }

  getRouteMap(
    routeId: number,
    opts?: { at_time?: string }
  ): Observable<{
    success: boolean;
    data: { route: GeoJSON.Feature; events: GeoJSON.FeatureCollection };
  }> {
    let params = new HttpParams();
    if (opts?.at_time) params = params.set('at_time', opts.at_time);
    return this.http.get<{
      success: boolean;
      data: { route: GeoJSON.Feature; events: GeoJSON.FeatureCollection };
    }>(`${this.apiUrl}/routes/${routeId}/map`, { params });
  }

  listSectionLinks(routeId: number): Observable<{ success: boolean; data: LrsSectionLink[] }> {
    return this.http.get<{ success: boolean; data: LrsSectionLink[] }>(
      `${this.apiUrl}/routes/${routeId}/section-links`
    );
  }

  generateChainageMarkers(
    routeId: number,
    intervalKm: number,
    persist = false
  ): Observable<{ success: boolean; data: GeoJSON.FeatureCollection }> {
    const params = new HttpParams()
      .set('interval_km', String(intervalKm))
      .set('persist', String(persist));
    return this.http.post<{ success: boolean; data: GeoJSON.FeatureCollection }>(
      `${this.apiUrl}/routes/${routeId}/chainage-markers`,
      {},
      { params }
    );
  }

  locate(routeId: number, lng: number, lat: number): Observable<{ success: boolean; data: LrsLocateResult }> {
    return this.http.post<{ success: boolean; data: LrsLocateResult }>(
      `${this.apiUrl}/routes/${routeId}/locate`,
      { lng, lat }
    );
  }

  interpolate(
    routeId: number,
    measureKm: number
  ): Observable<{ success: boolean; data: LrsLocateResult }> {
    const params = new HttpParams().set('measure_km', String(measureKm));
    return this.http.get<{ success: boolean; data: LrsLocateResult }>(
      `${this.apiUrl}/routes/${routeId}/interpolate`,
      { params }
    );
  }

  parseReference(reference: string): Observable<{ success: boolean; data: unknown }> {
    return this.http.post<{ success: boolean; data: unknown }>(`${this.apiUrl}/parse-reference`, {
      reference,
    });
  }

  listEvents(
    routeId: number,
    opts?: {
      fromKm?: number;
      toKm?: number;
      event_type?: string;
      at_time?: string;
      side?: string;
      abs_offset_min_m?: number;
      abs_offset_max_m?: number;
      abs_edge_offset_min_m?: number;
      abs_edge_offset_max_m?: number;
    }
  ): Observable<{ success: boolean; data: GeoJSON.FeatureCollection }> {
    let params = new HttpParams();
    if (opts?.fromKm != null) params = params.set('from_km', String(opts.fromKm));
    if (opts?.toKm != null) params = params.set('to_km', String(opts.toKm));
    if (opts?.event_type) params = params.set('event_type', opts.event_type);
    if (opts?.at_time) params = params.set('at_time', opts.at_time);
    if (opts?.side) params = params.set('side', opts.side);
    if (opts?.abs_offset_min_m != null) params = params.set('abs_offset_min_m', String(opts.abs_offset_min_m));
    if (opts?.abs_offset_max_m != null) params = params.set('abs_offset_max_m', String(opts.abs_offset_max_m));
    if (opts?.abs_edge_offset_min_m != null) params = params.set('abs_edge_offset_min_m', String(opts.abs_edge_offset_min_m));
    if (opts?.abs_edge_offset_max_m != null) params = params.set('abs_edge_offset_max_m', String(opts.abs_edge_offset_max_m));
    return this.http.get<{ success: boolean; data: GeoJSON.FeatureCollection }>(
      `${this.apiUrl}/routes/${routeId}/events`,
      { params }
    );
  }

  createEvent(
    routeId: number,
    body: {
      measure_m?: number;
      measure_km?: number;
      measure_to_m?: number;
      measure_to_km?: number;
      chainage?: string;
      event_type?: string;
      label?: string;
      description?: string;
      side?: string;
      offset_m?: number;
      edge_offset_m?: number;
      grade?: string;
      condition_grade?: string;
      asset_ref?: string;
      event_behavior?: string;
      valid_from?: string;
      valid_to?: string;
      properties?: Record<string, unknown>;
    }
  ): Observable<{ success: boolean; data: LrsEvent }> {
    const payload: Record<string, unknown> = { ...body };
    if (body.measure_km != null && body.measure_m == null) {
      payload['measure_m'] = body.measure_km * 1000;
    }
    if (body.measure_to_km != null && body.measure_to_m == null) {
      payload['measure_to_m'] = body.measure_to_km * 1000;
    }
    return this.http.post<{ success: boolean; data: LrsEvent }>(
      `${this.apiUrl}/routes/${routeId}/events`,
      payload
    );
  }

  getEvent(eventId: number): Observable<{ success: boolean; data: LrsEvent }> {
    return this.http.get<{ success: boolean; data: LrsEvent }>(`${this.apiUrl}/events/${eventId}`);
  }

  updateEvent(
    eventId: number,
    body: {
      measure_m?: number;
      measure_km?: number;
      measure_to_m?: number | null;
      measure_to_km?: number | null;
      clear_measure_to?: boolean;
      event_type?: string;
      label?: string;
      description?: string;
      side?: string;
      offset_m?: number;
      edge_offset_m?: number;
      grade?: string;
      condition_grade?: string;
      condition?: string;
      lifecycle_status?: string;
      asset_ref?: string;
      properties?: Record<string, unknown>;
      installation_date?: string | null;
      commissioning_date?: string | null;
      last_inspection_date?: string | null;
      next_inspection_date?: string | null;
      replacement_date?: string | null;
      decommission_date?: string | null;
      expected_life_years?: number | null;
      owner_name?: string | null;
      material?: string | null;
    }
  ): Observable<{ success: boolean; data: LrsEvent }> {
    const payload: Record<string, unknown> = { ...body };
    if (body.measure_km != null && body.measure_m == null) {
      payload['measure_m'] = body.measure_km * 1000;
    }
    if (body.clear_measure_to) {
      payload['measure_to_m'] = null;
      payload['measure_to_km'] = null;
      delete payload['clear_measure_to'];
    } else if (body.measure_to_km != null && body.measure_to_m == null) {
      payload['measure_to_m'] = body.measure_to_km * 1000;
    } else if (body.measure_to_km === null) {
      payload['measure_to_m'] = null;
    }
    return this.http.put<{ success: boolean; data: LrsEvent }>(
      `${this.apiUrl}/events/${eventId}`,
      payload
    );
  }

  listCalibrationPoints(routeId: number): Observable<{ success: boolean; data: LrsCalibrationPoint[] }> {
    return this.http.get<{ success: boolean; data: LrsCalibrationPoint[] }>(
      `${this.apiUrl}/routes/${routeId}/calibration-points`
    );
  }

  createCalibrationPoint(
    routeId: number,
    body: {
      measure_m?: number;
      measure_km?: number;
      lng?: number;
      lat?: number;
      point_type?: string;
      label?: string;
    }
  ): Observable<{ success: boolean; data: LrsCalibrationPoint }> {
    const payload: Record<string, unknown> = { ...body };
    if (body.measure_km != null && body.measure_m == null) {
      payload['measure_m'] = body.measure_km * 1000;
    }
    return this.http.post<{ success: boolean; data: LrsCalibrationPoint }>(
      `${this.apiUrl}/routes/${routeId}/calibration-points`,
      payload
    );
  }

  dynamicSegment(
    routeId: number,
    fromKm: number,
    toKm: number
  ): Observable<{ success: boolean; data: LrsDynamicSegmentResult }> {
    const params = new HttpParams()
      .set('from_km', String(fromKm))
      .set('to_km', String(toKm));
    return this.http.get<{ success: boolean; data: LrsDynamicSegmentResult }>(
      `${this.apiUrl}/routes/${routeId}/dynamic-segment`,
      { params }
    );
  }

  validateRouteQc(routeId: number): Observable<{ success: boolean; data: LrsQcReport }> {
    return this.http.get<{ success: boolean; data: LrsQcReport }>(
      `${this.apiUrl}/routes/${routeId}/qc`
    );
  }

  validateNetworkQc(opts?: {
    org_id?: number;
    project_id?: string;
    route_id?: number;
    snap_m?: number;
    gap_max_m?: number;
  }): Observable<{ success: boolean; data: LrsNetworkQcReport }> {
    let params = new HttpParams();
    if (opts?.org_id != null) params = params.set('org_id', String(opts.org_id));
    if (opts?.project_id) params = params.set('project_id', opts.project_id);
    if (opts?.route_id != null) params = params.set('route_id', String(opts.route_id));
    if (opts?.snap_m != null) params = params.set('snap_m', String(opts.snap_m));
    if (opts?.gap_max_m != null) params = params.set('gap_max_m', String(opts.gap_max_m));
    return this.http.get<{ success: boolean; data: LrsNetworkQcReport }>(`${this.apiUrl}/qc/network`, { params });
  }

  calibrationAccuracyReport(routeId: number): Observable<{ success: boolean; data: LrsCalibrationAccuracyReport }> {
    return this.http.get<{ success: boolean; data: LrsCalibrationAccuracyReport }>(
      `${this.apiUrl}/routes/${routeId}/calibration-accuracy`
    );
  }

  surveyDeviationReport(
    routeId: number,
    body: {
      observations: { lng: number; lat: number; field_measure_km?: number; field_chainage?: string; label?: string }[];
      max_error_m_per_km?: number;
      max_abs_deviation_m?: number;
      max_lateral_offset_m?: number;
    }
  ): Observable<{ success: boolean; data: LrsSurveyDeviationReport }> {
    return this.http.post<{ success: boolean; data: LrsSurveyDeviationReport }>(
      `${this.apiUrl}/routes/${routeId}/survey-compare`,
      body
    );
  }

  acceptanceEvidenceReport(
    routeId: number
  ): Observable<{ success: boolean; data: LrsAcceptanceEvidenceReport }> {
    return this.http.get<{ success: boolean; data: LrsAcceptanceEvidenceReport }>(
      `${this.apiUrl}/routes/${routeId}/acceptance-evidence`
    );
  }

  bulkImportAssets(
    routeId: number,
    body: { assets?: Record<string, unknown>[]; csv?: string }
  ): Observable<{ success: boolean; data: LrsBulkImportResult }> {
    return this.http.post<{ success: boolean; data: LrsBulkImportResult }>(
      `${this.apiUrl}/routes/${routeId}/import/assets`,
      body
    );
  }

  listLrsReports(routeId: number): Observable<{ success: boolean; data: LrsReportCatalogEntry[] }> {
    return this.http.get<{ success: boolean; data: LrsReportCatalogEntry[] }>(
      `${this.apiUrl}/routes/${routeId}/reports`
    );
  }

  getLrsReportJson(
    routeId: number,
    reportType: string,
    opts?: { fromKm?: number; toKm?: number }
  ): Observable<{ success: boolean; data: LrsExportReport }> {
    let params = new HttpParams().set('format', 'json');
    if (opts?.fromKm != null) params = params.set('from_km', String(opts.fromKm));
    if (opts?.toKm != null) params = params.set('to_km', String(opts.toKm));
    return this.http.get<{ success: boolean; data: LrsExportReport }>(
      `${this.apiUrl}/routes/${routeId}/reports/${reportType}`,
      { params }
    );
  }

  downloadLrsReport(
    routeId: number,
    reportType: string,
    format: 'csv' | 'xlsx',
    opts?: { fromKm?: number; toKm?: number }
  ): Observable<Blob> {
    let params = new HttpParams().set('format', format);
    if (opts?.fromKm != null) params = params.set('from_km', String(opts.fromKm));
    if (opts?.toKm != null) params = params.set('to_km', String(opts.toKm));
    return this.http.get(`${this.apiUrl}/routes/${routeId}/reports/${reportType}`, {
      params,
      responseType: 'blob',
    });
  }

  attachDocumentLrs(
    documentId: number,
    body: {
      route_id: number;
      measure_km?: number;
      measure_m?: number;
      chainage?: string;
      from_km?: number;
      to_km?: number;
    }
  ): Observable<{ success: boolean; data: unknown }> {
    return this.http.put<{ success: boolean; data: unknown }>(
      `${this.apiUrl}/documents/${documentId}/lrs`,
      body
    );
  }

  listDocumentsByLrs(params: {
    route_id?: number;
    from_km?: number;
    to_km?: number;
  }): Observable<{ success: boolean; data: LrsDocumentRef[] }> {
    let httpParams = new HttpParams();
    if (params.route_id != null) httpParams = httpParams.set('route_id', String(params.route_id));
    if (params.from_km != null) httpParams = httpParams.set('from_km', String(params.from_km));
    if (params.to_km != null) httpParams = httpParams.set('to_km', String(params.to_km));
    return this.http.get<{ success: boolean; data: LrsDocumentRef[] }>(`${this.apiUrl}/documents`, {
      params: httpParams,
    });
  }

  formatRouteReference(routeCode: string, measureKm: number): string {
    return `${routeCode} @ km ${measureKm.toFixed(3)}`;
  }

  gpsToChainage(
    routeId: number,
    lng: number,
    lat: number
  ): Observable<{ success: boolean; data: LrsLocateResult & { reference?: string } }> {
    return this.http.post<{ success: boolean; data: LrsLocateResult & { reference?: string } }>(`${this.apiUrl}/routes/${routeId}/gps-to-chainage`, { lng, lat });
  }

  corridorInventory(
    routeId: number,
    fromKm: number,
    toKm: number,
    opts?: { side?: string; abs_offset_min_m?: number; abs_offset_max_m?: number }
  ): Observable<{ success: boolean; data: LrsCorridorInventory }> {
    let params = new HttpParams().set('from_km', String(fromKm)).set('to_km', String(toKm));
    if (opts?.side) params = params.set('side', opts.side);
    if (opts?.abs_offset_min_m != null) params = params.set('abs_offset_min_m', String(opts.abs_offset_min_m));
    if (opts?.abs_offset_max_m != null) params = params.set('abs_offset_max_m', String(opts.abs_offset_max_m));
    return this.http.get<{ success: boolean; data: LrsCorridorInventory }>(
      `${this.apiUrl}/routes/${routeId}/corridor`,
      { params }
    );
  }

  listMeasurementRuns(routeId: number): Observable<{ success: boolean; data: LrsMeasurementRun[] }> {
    return this.http.get<{ success: boolean; data: LrsMeasurementRun[] }>(
      `${this.apiUrl}/routes/${routeId}/measurement-runs`
    );
  }

  createMeasurementRun(
    routeId: number,
    body: Partial<LrsMeasurementRun>
  ): Observable<{ success: boolean; data: LrsMeasurementRun }> {
    return this.http.post<{ success: boolean; data: LrsMeasurementRun }>(`${this.apiUrl}/routes/${routeId}/measurement-runs`, body);
  }

  listPavementConditions(
    routeId: number,
    opts?: { fromKm?: number; toKm?: number; run_id?: number }
  ): Observable<{ success: boolean; data: LrsPavementCondition[] }> {
    let params = new HttpParams();
    if (opts?.fromKm != null) params = params.set('from_km', String(opts.fromKm));
    if (opts?.toKm != null) params = params.set('to_km', String(opts.toKm));
    if (opts?.run_id != null) params = params.set('run_id', String(opts.run_id));
    return this.http.get<{ success: boolean; data: LrsPavementCondition[] }>(
      `${this.apiUrl}/routes/${routeId}/pavement-conditions`,
      { params }
    );
  }

  createPavementCondition(
    routeId: number,
    body: Record<string, unknown>
  ): Observable<{ success: boolean; data: LrsPavementCondition }> {
    return this.http.post<{ success: boolean; data: LrsPavementCondition }>(`${this.apiUrl}/routes/${routeId}/pavement-conditions`, body);
  }

  listMaintenanceEvents(
    routeId: number,
    opts?: { fromKm?: number; toKm?: number }
  ): Observable<{ success: boolean; data: LrsMaintenanceEvent[] }> {
    let params = new HttpParams();
    if (opts?.fromKm != null) params = params.set('from_km', String(opts.fromKm));
    if (opts?.toKm != null) params = params.set('to_km', String(opts.toKm));
    return this.http.get<{ success: boolean; data: LrsMaintenanceEvent[] }>(
      `${this.apiUrl}/routes/${routeId}/maintenance-events`,
      { params }
    );
  }

  createMaintenanceEvent(
    routeId: number,
    body: Record<string, unknown>
  ): Observable<{ success: boolean; data: LrsMaintenanceEvent }> {
    return this.http.post<{ success: boolean; data: LrsMaintenanceEvent }>(`${this.apiUrl}/routes/${routeId}/maintenance-events`, body);
  }

  listUtilityAssets(
    routeId: number,
    opts?: {
      fromKm?: number;
      toKm?: number;
      utility_type?: string;
      side?: string;
      abs_offset_min_m?: number;
      abs_offset_max_m?: number;
      abs_edge_offset_min_m?: number;
      abs_edge_offset_max_m?: number;
    }
  ): Observable<{ success: boolean; data: LrsUtilityAsset[] }> {
    let params = new HttpParams();
    if (opts?.fromKm != null) params = params.set('from_km', String(opts.fromKm));
    if (opts?.toKm != null) params = params.set('to_km', String(opts.toKm));
    if (opts?.utility_type) params = params.set('utility_type', opts.utility_type);
    if (opts?.side) params = params.set('side', opts.side);
    if (opts?.abs_offset_min_m != null) params = params.set('abs_offset_min_m', String(opts.abs_offset_min_m));
    if (opts?.abs_offset_max_m != null) params = params.set('abs_offset_max_m', String(opts.abs_offset_max_m));
    if (opts?.abs_edge_offset_min_m != null) params = params.set('abs_edge_offset_min_m', String(opts.abs_edge_offset_min_m));
    if (opts?.abs_edge_offset_max_m != null) params = params.set('abs_edge_offset_max_m', String(opts.abs_edge_offset_max_m));
    return this.http.get<{ success: boolean; data: LrsUtilityAsset[] }>(
      `${this.apiUrl}/routes/${routeId}/utility-assets`,
      { params }
    );
  }

  locateAssetsAtOffset(
    routeId: number,
    opts: {
      measure_km?: number;
      chainage?: string;
      side?: string;
      edge_offset_m?: number;
      tolerance_m?: number;
      edge_tolerance_m?: number;
    }
  ): Observable<{ success: boolean; data: LrsOffsetLocateResult }> {
    let params = new HttpParams();
    if (opts.measure_km != null) params = params.set('measure_km', String(opts.measure_km));
    if (opts.chainage) params = params.set('chainage', opts.chainage);
    if (opts.side) params = params.set('side', opts.side);
    if (opts.edge_offset_m != null) params = params.set('edge_offset_m', String(opts.edge_offset_m));
    if (opts.tolerance_m != null) params = params.set('tolerance_m', String(opts.tolerance_m));
    if (opts.edge_tolerance_m != null) params = params.set('edge_tolerance_m', String(opts.edge_tolerance_m));
    return this.http.get<{ success: boolean; data: LrsOffsetLocateResult }>(
      `${this.apiUrl}/routes/${routeId}/assets-at-offset`,
      { params }
    );
  }

  getRouteKpis(routeId: number): Observable<{ success: boolean; data: LrsRouteKpis }> {
    return this.http.get<{ success: boolean; data: LrsRouteKpis }>(`${this.apiUrl}/routes/${routeId}/kpis`);
  }

  getCrossSection(
    routeId: number,
    opts?: { measure_m?: number; measure_km?: number; chainage?: string; tolerance_m?: number }
  ): Observable<{ success: boolean; data: LrsCrossSection }> {
    let params = new HttpParams();
    if (opts?.measure_m != null) params = params.set('measure_m', String(opts.measure_m));
    if (opts?.measure_km != null) params = params.set('measure_km', String(opts.measure_km));
    if (opts?.chainage) params = params.set('chainage', opts.chainage);
    if (opts?.tolerance_m != null) params = params.set('tolerance_m', String(opts.tolerance_m));
    return this.http.get<{ success: boolean; data: LrsCrossSection }>(
      `${this.apiUrl}/routes/${routeId}/cross-section`,
      { params }
    );
  }

  getVerticalProfile(
    routeId: number,
    opts?: { from_km?: number; to_km?: number; step_m?: number; measure_m?: number }
  ): Observable<{ success: boolean; data: LrsVerticalProfile }> {
    let params = new HttpParams();
    if (opts?.from_km != null) params = params.set('from_km', String(opts.from_km));
    if (opts?.to_km != null) params = params.set('to_km', String(opts.to_km));
    if (opts?.step_m != null) params = params.set('step_m', String(opts.step_m));
    if (opts?.measure_m != null) params = params.set('measure_m', String(opts.measure_m));
    return this.http.get<{ success: boolean; data: LrsVerticalProfile }>(
      `${this.apiUrl}/routes/${routeId}/vertical-profile`,
      { params }
    );
  }

  listVerticalPvis(routeId: number): Observable<{ success: boolean; data: LrsVerticalPvi[] }> {
    return this.http.get<{ success: boolean; data: LrsVerticalPvi[] }>(
      `${this.apiUrl}/routes/${routeId}/vertical-pvis`
    );
  }

  createVerticalPvi(
    routeId: number,
    body: Record<string, unknown>
  ): Observable<{ success: boolean; data: LrsVerticalPvi }> {
    return this.http.post<{ success: boolean; data: LrsVerticalPvi }>(
      `${this.apiUrl}/routes/${routeId}/vertical-pvis`,
      body
    );
  }

  updateVerticalPvi(
    pviId: number,
    body: Record<string, unknown>
  ): Observable<{ success: boolean; data: LrsVerticalPvi }> {
    return this.http.put<{ success: boolean; data: LrsVerticalPvi }>(
      `${this.apiUrl}/vertical-pvis/${pviId}`,
      body
    );
  }

  deleteVerticalPvi(pviId: number): Observable<{ success: boolean; data: { id: number; deleted: boolean } }> {
    return this.http.delete<{ success: boolean; data: { id: number; deleted: boolean } }>(
      `${this.apiUrl}/vertical-pvis/${pviId}`
    );
  }

  listGroundProfile(routeId: number): Observable<{ success: boolean; data: LrsGroundSample[] }> {
    return this.http.get<{ success: boolean; data: LrsGroundSample[] }>(
      `${this.apiUrl}/routes/${routeId}/ground-profile`
    );
  }

  replaceGroundProfile(
    routeId: number,
    body: { samples: Array<Record<string, unknown>>; replace?: boolean }
  ): Observable<{ success: boolean; data: { count: number; samples: LrsGroundSample[] } }> {
    return this.http.post<{ success: boolean; data: { count: number; samples: LrsGroundSample[] } }>(
      `${this.apiUrl}/routes/${routeId}/ground-profile`,
      body
    );
  }

  createUtilityAsset(
    routeId: number,
    body: Record<string, unknown>
  ): Observable<{ success: boolean; data: LrsUtilityAsset }> {
    return this.http.post<{ success: boolean; data: LrsUtilityAsset }>(`${this.apiUrl}/routes/${routeId}/utility-assets`, body);
  }

  updateUtilityAsset(
    assetId: number,
    body: Record<string, unknown>
  ): Observable<{ success: boolean; data: LrsUtilityAsset }> {
    return this.http.put<{ success: boolean; data: LrsUtilityAsset }>(
      `${this.apiUrl}/utility-assets/${assetId}`,
      body
    );
  }

  getUtilityAsset(assetId: number): Observable<{ success: boolean; data: LrsUtilityAsset }> {
    return this.http.get<{ success: boolean; data: LrsUtilityAsset }>(
      `${this.apiUrl}/utility-assets/${assetId}`
    );
  }

  retireUtilityAsset(assetId: number): Observable<{ success: boolean; data: LrsUtilityAsset }> {
    return this.http.post<{ success: boolean; data: LrsUtilityAsset }>(
      `${this.apiUrl}/utility-assets/${assetId}/retire`,
      {}
    );
  }

  getAssetDetail(
    kind: 'event' | 'utility',
    id: number
  ): Observable<{ success: boolean; data: LrsAssetDetail }> {
    return this.http.get<{ success: boolean; data: LrsAssetDetail }>(
      `${this.apiUrl}/assets/${kind}/${id}/detail`
    );
  }

  createAssetLink(
    kind: 'event' | 'utility',
    id: number,
    body: { to_kind: 'event' | 'utility'; to_id: number; relation?: string }
  ): Observable<{ success: boolean; data: LrsAssetLink }> {
    return this.http.post<{ success: boolean; data: LrsAssetLink }>(
      `${this.apiUrl}/assets/${kind}/${id}/links`,
      body
    );
  }

  deleteAssetLink(linkId: number): Observable<{ success: boolean }> {
    return this.http.delete<{ success: boolean }>(`${this.apiUrl}/links/${linkId}`);
  }

  uploadAssetDocument(
    kind: 'event' | 'utility',
    id: number,
    file: File,
    docType?: string,
    caption?: string
  ): Observable<{ success: boolean; data: LrsAssetDocument }> {
    const form = new FormData();
    form.append('file', file);
    if (docType) form.append('doc_type', docType);
    if (caption) form.append('caption', caption);
    return this.http.post<{ success: boolean; data: LrsAssetDocument }>(
      `${this.apiUrl}/assets/${kind}/${id}/documents`,
      form
    );
  }

  deleteAssetDocument(docId: number): Observable<{ success: boolean }> {
    return this.http.delete<{ success: boolean }>(`${this.apiUrl}/asset-documents/${docId}`);
  }

  listCarriageways(routeId: number): Observable<{ success: boolean; data: LrsCarriageway[] }> {
    return this.http.get<{ success: boolean; data: LrsCarriageway[] }>(
      `${this.apiUrl}/routes/${routeId}/carriageways`
    );
  }

  createCarriageway(
    routeId: number,
    body: Record<string, unknown>
  ): Observable<{ success: boolean; data: LrsCarriageway }> {
    return this.http.post<{ success: boolean; data: LrsCarriageway }>(`${this.apiUrl}/routes/${routeId}/carriageways`, body);
  }

  extendRoute(
    routeId: number,
    body: {
      position: 'start' | 'end';
      extend_m?: number;
      delta_m?: number;
      event_behavior?: string;
      effective_at?: string;
    }
  ): Observable<{ success: boolean; data: LrsRouteExtensionResult }> {
    return this.http.post<{ success: boolean; data: LrsRouteExtensionResult }>(`${this.apiUrl}/routes/${routeId}/extend`, body);
  }

  adjustRouteLength(
    routeId: number,
    body: {
      position: 'start' | 'end';
      delta_m: number;
      event_behavior?: string;
      effective_at?: string;
    }
  ): Observable<{ success: boolean; data: LrsRouteExtensionResult }> {
    return this.http.post<{ success: boolean; data: LrsRouteExtensionResult }>(
      `${this.apiUrl}/routes/${routeId}/adjust-length`,
      body
    );
  }

  setRouteTargetLength(
    routeId: number,
    body: {
      target_length_km?: number;
      target_length_m?: number;
      position?: 'start' | 'end';
      event_behavior?: string;
    }
  ): Observable<{ success: boolean; data: LrsRouteExtensionResult }> {
    return this.http.post<{ success: boolean; data: LrsRouteExtensionResult }>(
      `${this.apiUrl}/routes/${routeId}/set-length`,
      body
    );
  }

  listRouteExtensions(routeId: number): Observable<{ success: boolean; data: LrsRouteExtensionLog[] }> {
    return this.http.get<{ success: boolean; data: LrsRouteExtensionLog[] }>(`${this.apiUrl}/routes/${routeId}/extensions`);
  }

  detectClashes(
    routeId: number,
    fromKm: number,
    toKm: number,
    opts?: {
      buffer_m?: number;
      max_depth_m?: number;
      min_vertical_sep_m?: number;
      min_3d_sep_m?: number;
      format?: 'geojson';
    }
  ): Observable<{ success: boolean; data: LrsClashReport | GeoJSON.FeatureCollection }> {
    let params = new HttpParams().set('from_km', String(fromKm)).set('to_km', String(toKm));
    if (opts?.buffer_m != null) params = params.set('buffer_m', String(opts.buffer_m));
    if (opts?.max_depth_m != null) params = params.set('max_depth_m', String(opts.max_depth_m));
    if (opts?.min_vertical_sep_m != null) params = params.set('min_vertical_sep_m', String(opts.min_vertical_sep_m));
    if (opts?.min_3d_sep_m != null) params = params.set('min_3d_sep_m', String(opts.min_3d_sep_m));
    if (opts?.format) params = params.set('format', opts.format);
    return this.http.get<{ success: boolean; data: LrsClashReport | GeoJSON.FeatureCollection }>(
      `${this.apiUrl}/routes/${routeId}/clash-detection`,
      { params }
    );
  }

  listRouteSnapshots(routeId: number): Observable<{ success: boolean; data: LrsRouteSnapshot[] }> {
    return this.http.get<{ success: boolean; data: LrsRouteSnapshot[] }>(
      `${this.apiUrl}/routes/${routeId}/snapshots`
    );
  }

  captureRouteSnapshot(
    routeId: number,
    reason = 'manual'
  ): Observable<{ success: boolean; data: LrsRouteSnapshot }> {
    return this.http.post<{ success: boolean; data: LrsRouteSnapshot }>(
      `${this.apiUrl}/routes/${routeId}/snapshots`,
      { reason }
    );
  }

  downloadShapefile(
    routeId: number,
    layer: 'all' | 'route' | 'events' | 'utilities' | 'clash' = 'all',
    opts?: { fromKm?: number; toKm?: number }
  ): Observable<Blob> {
    let params = new HttpParams().set('layer', layer);
    if (opts?.fromKm != null) params = params.set('from_km', String(opts.fromKm));
    if (opts?.toKm != null) params = params.set('to_km', String(opts.toKm));
    return this.http.get(`${this.apiUrl}/routes/${routeId}/export/shapefile`, {
      params,
      responseType: 'blob',
    });
  }

  importShapefile(
    file: File,
    body?: {
      route_id?: number;
      route_code?: string;
      route_name?: string;
      project_id?: string;
      network_id?: number;
      subnetwork_id?: number;
    }
  ): Observable<{ success: boolean; data: LrsShapefileImportResult }> {
    const fd = new FormData();
    fd.append('file', file, file.name);
    if (body?.route_id != null) fd.append('route_id', String(body.route_id));
    if (body?.route_code) fd.append('route_code', body.route_code);
    if (body?.route_name) fd.append('route_name', body.route_name);
    if (body?.project_id) fd.append('project_id', body.project_id);
    if (body?.network_id != null) fd.append('network_id', String(body.network_id));
    if (body?.subnetwork_id != null) fd.append('subnetwork_id', String(body.subnetwork_id));
    return this.http.post<{ success: boolean; data: LrsShapefileImportResult }>(
      `${this.apiUrl}/import/shapefile`,
      fd
    );
  }

  createExcavationPermit(
    routeId: number,
    body: Record<string, unknown>
  ): Observable<{ success: boolean; data: { permit: unknown; clash_report: LrsClashReport } }> {
    return this.http.post<{ success: boolean; data: { permit: unknown; clash_report: LrsClashReport } }>(`${this.apiUrl}/routes/${routeId}/excavation-permits`, body);
  }

  geometryAtOffset(
    routeId: number,
    measureKm: number,
    lateralOffsetM: number,
    side?: string
  ): Observable<{ success: boolean; data: LrsOffsetGeometry }> {
    let params = new HttpParams()
      .set('measure_km', String(measureKm))
      .set('lateral_offset_m', String(lateralOffsetM));
    if (side) params = params.set('side', side);
    return this.http.get<{ success: boolean; data: LrsOffsetGeometry }>(`${this.apiUrl}/routes/${routeId}/geometry-at-offset`, { params });
  }

  reapplyEventOffsets(routeId: number): Observable<{ success: boolean; data: { route_id: number; events_updated: number } }> {
    return this.http.post<{ success: boolean; data: { route_id: number; events_updated: number } }>(
      `${this.apiUrl}/routes/${routeId}/events/reapply-offsets`,
      {}
    );
  }

  /** MVT vector-tile URL template ({z}/{x}/{y}) for an OL VectorTile source. */
  vectorTileUrlTemplate(routeId?: number | null): string {
    return routeId != null
      ? `${this.apiUrl}/routes/${routeId}/tiles/{z}/{x}/{y}.mvt`
      : `${this.apiUrl}/tiles/{z}/{x}/{y}.mvt`;
  }

  /** Server-side clusters (GeoJSON) — method: dbscan | grid | heatmap | utility_network. */
  clusterEvents(
    routeId: number | null,
    opts: {
      method?: 'dbscan' | 'grid' | 'heatmap' | 'utility_network';
      zoom?: number;
      eps_m?: number;
      cell_m?: number;
      min_points?: number;
      event_type?: string;
      radius_px?: number;
      bbox?: { minLng: number; minLat: number; maxLng: number; maxLat: number };
    } = {}
  ): Observable<{ success: boolean; data: LrsClusterCollection }> {
    let params = new HttpParams();
    if (opts.method) params = params.set('method', opts.method);
    if (opts.zoom != null) params = params.set('zoom', String(opts.zoom));
    if (opts.eps_m != null) params = params.set('eps_m', String(opts.eps_m));
    if (opts.cell_m != null) params = params.set('cell_m', String(opts.cell_m));
    if (opts.min_points != null) params = params.set('min_points', String(opts.min_points));
    if (opts.event_type) params = params.set('event_type', opts.event_type);
    if (opts.radius_px != null) params = params.set('radius_px', String(opts.radius_px));
    if (opts.bbox) {
      params = params
        .set('min_lng', String(opts.bbox.minLng))
        .set('min_lat', String(opts.bbox.minLat))
        .set('max_lng', String(opts.bbox.maxLng))
        .set('max_lat', String(opts.bbox.maxLat));
    }
    const url = routeId != null ? `${this.apiUrl}/routes/${routeId}/clusters` : `${this.apiUrl}/clusters`;
    return this.http.get<{ success: boolean; data: LrsClusterCollection }>(url, { params });
  }

  // --- Per-lane geometry (merge/diverge/weave) ---
  listLanes(routeId: number): Observable<{ success: boolean; data: LrsLane[] }> {
    return this.http.get<{ success: boolean; data: LrsLane[] }>(`${this.apiUrl}/routes/${routeId}/lanes`);
  }

  createLane(routeId: number, body: Partial<LrsLane> & Record<string, unknown>): Observable<{ success: boolean; data: LrsLane }> {
    return this.http.post<{ success: boolean; data: LrsLane }>(`${this.apiUrl}/routes/${routeId}/lanes`, body);
  }

  generateLanes(
    routeId: number,
    carriagewayId: number,
    body?: { lane_count?: number; width_m?: number; replace?: boolean }
  ): Observable<{ success: boolean; data: { lanes: LrsLane[]; lane_count: number } }> {
    return this.http.post<{ success: boolean; data: { lanes: LrsLane[]; lane_count: number } }>(
      `${this.apiUrl}/routes/${routeId}/carriageways/${carriagewayId}/lanes/generate`,
      body || {}
    );
  }

  deleteLane(laneId: number): Observable<{ success: boolean; data: unknown }> {
    return this.http.delete<{ success: boolean; data: unknown }>(`${this.apiUrl}/lanes/${laneId}`);
  }

  // --- Parent/child road geometry segments ---
  listRouteSegments(routeId: number): Observable<{ success: boolean; data: LrsRouteSegment[] }> {
    return this.http.get<{ success: boolean; data: LrsRouteSegment[] }>(`${this.apiUrl}/routes/${routeId}/segments`);
  }

  addRouteSegment(
    routeId: number,
    body: { geometry: GeoJSON.LineString; segment_code?: string; seq?: number; rebuild?: boolean }
  ): Observable<{ success: boolean; data: LrsRouteSegment & { rebuild?: { length_m: number } } }> {
    return this.http.post<{ success: boolean; data: LrsRouteSegment & { rebuild?: { length_m: number } } }>(
      `${this.apiUrl}/routes/${routeId}/segments`,
      body
    );
  }

  rebuildRouteGeometry(routeId: number): Observable<{ success: boolean; data: { length_m: number; geometry: GeoJSON.LineString } }> {
    return this.http.post<{ success: boolean; data: { length_m: number; geometry: GeoJSON.LineString } }>(
      `${this.apiUrl}/routes/${routeId}/segments/rebuild`,
      {}
    );
  }

  assetsNearPoint(
    routeId: number,
    lng: number,
    lat: number,
    radiusM = 500
  ): Observable<{ success: boolean; data: LrsAssetsNearResult }> {
    return this.http.post<{ success: boolean; data: LrsAssetsNearResult }>(`${this.apiUrl}/routes/${routeId}/assets-near-point`, {
      lng,
      lat,
      radius_m: radiusM,
    });
  }

  retireEvent(eventId: number, validTo?: string): Observable<{ success: boolean; data: unknown }> {
    return this.http.post<{ success: boolean; data: unknown }>(`${this.apiUrl}/events/${eventId}/retire`, { valid_to: validTo });
  }

  listExcavationPermits(routeId: number): Observable<{ success: boolean; data: LrsExcavationPermit[] }> {
    return this.http.get<{ success: boolean; data: LrsExcavationPermit[] }>(
      `${this.apiUrl}/routes/${routeId}/excavation-permits`
    );
  }

  realignRoute(
    routeId: number,
    body: { geometry: GeoJSON.LineString; notes?: string }
  ): Observable<{ success: boolean; data: LrsRealignResult }> {
    return this.http.post<{ success: boolean; data: LrsRealignResult }>(
      `${this.apiUrl}/routes/${routeId}/realign`,
      body
    );
  }
}

export interface LrsRouteExtensionResult {
  route_id: number;
  position: string;
  delta_m?: number;
  extend_m: number;
  event_behavior: string;
  events_affected: number;
  unchanged?: boolean;
  previous: { origin_measure_m: number; length_m: number; end_measure_m: number };
  current: { origin_measure_m: number; length_m: number; end_measure_m: number };
}

export interface LrsRouteExtensionLog {
  id: number;
  route_id: number;
  position: string;
  extend_m: number;
  event_behavior: string;
  events_affected: number;
  created_at: string;
}

export interface LrsClashItem {
  severity: string;
  code: string;
  message: string;
  layer?: string;
  utility_type?: string;
  measure_km?: number;
  measure_m?: number;
  depth_m?: number | null;
  horizontal_sep_m?: number | null;
  vertical_sep_m?: number | null;
  three_d_sep_m?: number | null;
  point?: GeoJSON.Point;
}

export interface LrsClashReport {
  ok: boolean;
  clash_count: number;
  critical_count: number;
  high_count: number;
  recommendation: string;
  clashes: LrsClashItem[];
  from_km: number;
  to_km: number;
  zone_geometry?: GeoJSON.LineString;
  clash_points?: GeoJSON.FeatureCollection;
  three_d?: boolean;
  min_vertical_sep_m?: number;
  min_3d_sep_m?: number;
}

export interface LrsRouteSnapshot {
  id: number;
  route_id: number;
  captured_at: string;
  reason: string;
  origin_measure_m: number;
  length_m: number;
  created_by?: string;
  geometry?: GeoJSON.LineString;
}

export interface LrsShapefileImportResult {
  ok: boolean;
  routes: number;
  events: number;
  route_id?: number;
  route?: LrsRoute;
  errors: { label?: string; error: string }[];
}

export interface LrsExcavationPermit {
  id: number;
  route_id: number;
  permit_ref?: string;
  from_measure_m: number;
  to_measure_m: number;
  from_km?: number;
  to_km?: number;
  status: string;
  depth_m?: number;
  created_at: string;
  clash_summary?: LrsClashReport;
}

export interface LrsRealignResult {
  route_id: number;
  previous_length_m: number;
  new_length_m: number;
  length_delta_m: number;
}

export interface LrsDemoSeedResult {
  route_id: number;
  route_code: string;
  length_km: number;
  length_m: number;
  total_events?: number;
  qc_ok?: boolean;
  qc_issues?: number;
  clash_ok?: boolean;
  clash_count?: number;
  snapshot_id?: number | null;
  counts: Record<string, number>;
}

export interface LrsOffsetGeometry {
  measure_km: number;
  chainage_label: string;
  center_point?: GeoJSON.Point;
  offset_point?: GeoJSON.Point;
  offset_line?: GeoJSON.LineString;
  lateral_offset_m: number;
}

export interface LrsAssetsNearResult {
  locate: LrsLocateResult;
  reference: string;
  radius_m: number;
  count: number;
  items: Record<string, unknown>[];
}

export interface LrsCorridorInventory {
  from_km: number;
  to_km: number;
  from_measure_m?: number;
  to_measure_m?: number;
  route_segment?: GeoJSON.Feature;
  segment_length_m?: number;
  summary: Record<string, number>;
  layer_qa?: {
    layers: { id: string; label: string; count: number }[];
    notes: string[];
    ok: boolean;
  };
  highway_assets: GeoJSON.FeatureCollection;
  attribute_events: GeoJSON.FeatureCollection;
  pavement_conditions: LrsPavementCondition[];
  maintenance_events: LrsMaintenanceEvent[];
  utility_assets: LrsUtilityAsset[];
  documents: unknown[];
}

export interface LrsMeasurementRun {
  id: number;
  route_id: number;
  run_date: string;
  vehicle_id?: string;
  operator_name?: string;
  start_measure_m?: number;
  end_measure_m?: number;
  sampling_interval_m?: number;
}

export interface LrsPavementCondition {
  id: number;
  route_id: number;
  from_measure_m: number;
  to_measure_m: number;
  from_km?: number;
  to_km?: number;
  iri?: number;
  pci?: number;
  rut_depth_mm?: number;
  cracking_pct?: number;
  surface_type?: string;
  geometry?: GeoJSON.LineString;
}

export interface LrsMaintenanceEvent {
  id: number;
  route_id: number;
  work_type: string;
  from_measure_m: number;
  to_measure_m: number;
  work_date?: string;
  cost_inr?: number;
  contractor?: string;
  geometry?: GeoJSON.LineString;
}

export interface LrsOffsetLocateResult {
  route_id: number;
  route_code?: string;
  measure_m: number;
  measure_km: number;
  chainage_label: string;
  side?: string | null;
  edge_offset_m?: number | null;
  count: number;
  items: {
    kind: 'event' | 'utility';
    id: number;
    type?: string;
    label?: string;
    measure_m?: number;
    side?: string;
    offset_m?: number;
    edge_offset_m?: number;
    depth_m?: number;
    grade?: string;
  }[];
}

export interface LrsRouteKpis {
  route_id: number;
  route_code?: string;
  total: number;
  by_grade: { underground: number; overground: number; crossover: number };
  by_lifecycle: { operational: number; construction: number; planned: number; decommissioned: number };
  by_type: Record<string, number>;
}

export interface LrsCrossSectionAsset {
  kind: 'event' | 'utility';
  id: number;
  type?: string;
  label?: string;
  measure_m?: number;
  offset_m: number;
  edge_offset_m?: number | null;
  depth_m: number;
  diameter_mm?: number | null;
  grade?: string;
  side?: string | null;
}

export interface LrsRoadStrip {
  kind?: string;
  code?: string;
  lane_number?: number;
  side?: string;
  offset_from_m: number;
  offset_to_m: number;
  width_m: number;
}

export interface LrsRoadEnvelope {
  width_m: number;
  lane_count: number;
  synthesized?: boolean;
  median: LrsRoadStrip | null;
  shoulders: LrsRoadStrip[];
  lanes: LrsRoadStrip[];
  pavement_left_m: number;
  pavement_right_m: number;
  row_left_m: number;
  row_right_m: number;
}

export interface LrsCrossSection {
  route_id: number;
  route_code?: string;
  origin_m?: number;
  length_m?: number;
  measure_m: number;
  measure_km: number;
  chainage_label: string;
  tolerance_m: number;
  median_m: number;
  pavement_left_m: number;
  pavement_right_m: number;
  pavement_width_m: number;
  ground_elev_m?: number | null;
  design_elev_m?: number | null;
  road?: LrsRoadEnvelope;
  assets: LrsCrossSectionAsset[];
}

export interface LrsVerticalPvi {
  id: number;
  route_id: number;
  pvi_measure_m: number;
  pvi_elevation_m: number;
  grade_in_pct: number;
  grade_out_pct: number;
  curve_length_m: number;
  k_value?: number | null;
  curve_kind?: 'crest' | 'sag' | null;
  label?: string | null;
  algebraic_diff_pct?: number;
  t1_measure_m: number;
  t2_measure_m: number;
  t1_elevation_m: number;
  t2_elevation_m: number;
  chainage_label?: string;
  t1_label?: string;
  t2_label?: string;
}

export interface LrsGroundSample {
  id?: number;
  route_id?: number;
  measure_m: number;
  elevation_m: number;
  source?: string;
}

export interface LrsVerticalSample {
  measure_m: number;
  chainage_label: string;
  design_elev_m: number | null;
  ground_elev_m: number | null;
}

export interface LrsVerticalProfile {
  route_id: number;
  route_code?: string;
  origin_m: number;
  length_m: number;
  from_m: number;
  to_m: number;
  step_m: number;
  samples: LrsVerticalSample[];
  pvis: LrsVerticalPvi[];
  cursor?: LrsVerticalSample | null;
}

export interface LrsUtilityAsset {
  id: number;
  route_id: number;
  utility_type: string;
  measure_m: number;
  measure_to_m?: number;
  depth_m?: number;
  lateral_offset_m?: number;
  edge_offset_m?: number;
  side?: string;
  grade?: string;
  label?: string;
  diameter_mm?: number;
  lifecycle_status?: string;
  condition?: string;
  map_status?: string;
  remaining_life_years?: number | null;
  installation_date?: string | null;
  commissioning_date?: string | null;
  last_inspection_date?: string | null;
  next_inspection_date?: string | null;
  replacement_date?: string | null;
  decommission_date?: string | null;
  expected_life_years?: number | null;
  owner_name?: string | null;
  material?: string | null;
  geometry?: GeoJSON.Geometry;
  is_linear?: boolean;
  properties?: Record<string, unknown>;
  created_at?: string;
}

export interface LrsAssetLink {
  id: number;
  from_kind: 'event' | 'utility';
  from_id: number;
  to_kind: 'event' | 'utility';
  to_id: number;
  relation: string;
  other?: {
    kind: string;
    id: number;
    label?: string;
    type?: string | null;
    chainage_label?: string;
    route_id?: number;
  };
}

export interface LrsAssetDocument {
  id: number;
  asset_kind: string;
  asset_id: number;
  doc_type: string;
  file_name?: string;
  mime_type?: string;
  byte_size?: number;
  storage_path?: string;
  caption?: string;
  url?: string | null;
  created_at?: string;
}

export interface LrsAssetDetail {
  kind: 'event' | 'utility';
  asset: LrsEvent | LrsUtilityAsset;
  remaining_life_years?: number | null;
  map_status?: string;
  links: LrsAssetLink[];
  documents: LrsAssetDocument[];
  images: LrsAssetDocument[];
  files: LrsAssetDocument[];
  maintenance: LrsMaintenanceEvent[];
}

export interface LrsCarriageway {
  id: number;
  route_id: number;
  carriageway_code: string;
  direction: string;
  lane_count: number;
  from_measure_m: number;
  to_measure_m?: number;
  width_m?: number;
  lateral_offset_m?: number;
  geometry?: GeoJSON.LineString;
}
